/**
 * Camp Highlands terminology + cabin-number helpers.
 *
 * The frontend renders cabins and divisions according to Camp Highlands'
 * operational model, which is *not* the raw shape returned from CampMinder.
 * In particular, divisions are derived from a hard-coded cabin-number range
 * rather than from any CampMinder Division/BunkArea row, so that cabin "23"
 * is always Cubs regardless of how the underlying sync attributes it.
 *
 * Use these helpers everywhere a camper-facing surface needs to know which
 * division a cabin belongs to or how to display a cabin number.
 */

export type CampHighlandsDivision =
  | "Seniors"
  | "Juniors"
  | "Colts"
  | "Cubs"
  | "Rangers";

/** Display order for divisions on the Cabins page and other camper-facing surfaces. */
export const DIVISION_ORDER: readonly CampHighlandsDivision[] = [
  "Seniors",
  "Juniors",
  "Colts",
  "Cubs",
  "Rangers",
] as const;

/**
 * Cabin number range per division. Inclusive on both ends.
 *
 * Rangers is intentionally absent — Rangers campers physically sleep in the
 * Seniors range but are operationally a separate division. Look up Rangers
 * attribution from `Camper.division`, not from the cabin number.
 */
export const DIVISION_RANGES: Record<
  Exclude<CampHighlandsDivision, "Rangers">,
  readonly [number, number]
> = {
  Seniors: [1, 7],
  Juniors: [8, 14],
  Colts: [15, 20],
  Cubs: [21, 26],
} as const;

/**
 * Lookup table from cabin number (1-26) -> division. Useful for callers
 * that don't want to walk the ranges themselves.
 */
export const CABIN_NUMBER_TO_DIVISION: Readonly<
  Record<number, Exclude<CampHighlandsDivision, "Rangers">>
> = (() => {
  const map: Record<number, Exclude<CampHighlandsDivision, "Rangers">> = {};
  for (const [division, [start, end]] of Object.entries(DIVISION_RANGES) as Array<
    [Exclude<CampHighlandsDivision, "Rangers">, readonly [number, number]]
  >) {
    for (let n = start; n <= end; n++) map[n] = division;
  }
  return map;
})();

/** Lowest cabin number Camp Highlands uses. */
export const MIN_CABIN_NUMBER = 1;
/** Highest cabin number Camp Highlands uses. */
export const MAX_CABIN_NUMBER = 26;

/**
 * Parse a cabin name into its numeric value. Returns null if the name is
 * not a plain integer in the 1-26 range.
 */
export function parseCabinNumber(name: string | null | undefined): number | null {
  if (name == null) return null;
  const trimmed = String(name).trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  if (n < MIN_CABIN_NUMBER || n > MAX_CABIN_NUMBER) return null;
  return n;
}

/**
 * Format a cabin name for display: numeric names are zero-padded to two
 * digits ("5" -> "05", "23" -> "23"). Non-numeric names pass through
 * unchanged so that admin surfaces can still render labels like "LODGE"
 * or "Rangers" when needed.
 */
export function formatCabinNumber(name: string | null | undefined): string {
  if (name == null) return "";
  const n = parseCabinNumber(name);
  if (n == null) return String(name);
  return n.toString().padStart(2, "0");
}

/**
 * Resolve the Camp Highlands division for a numeric cabin number. Returns
 * null if the number is outside the 1-26 range. Callers that need Rangers
 * attribution must consult the camper's own division.
 */
export function divisionForCabinNumber(
  n: number | null | undefined,
): Exclude<CampHighlandsDivision, "Rangers"> | null {
  if (n == null) return null;
  return CABIN_NUMBER_TO_DIVISION[n] ?? null;
}

/**
 * Convenience: parse a cabin name and resolve its division in one step.
 */
export function divisionForCabinName(
  name: string | null | undefined,
): Exclude<CampHighlandsDivision, "Rangers"> | null {
  return divisionForCabinNumber(parseCabinNumber(name));
}

/**
 * Normalize a CampMinder division name to its Camp Highlands display form.
 * "Senior" and "Seniors" both render as "Seniors"; everything else is
 * returned unchanged.
 */
export function normalizeDivisionName(name: string | null | undefined): string {
  if (name == null) return "";
  const trimmed = String(name).trim();
  if (/^seniors?$/i.test(trimmed)) return "Seniors";
  return trimmed;
}
